/**
 * offlineQueue.js — Módulo de cola offline para Chizy Ops.
 * Guarda requests POST/PUT en IndexedDB cuando la red no está disponible.
 * Sincroniza automáticamente al volver la red (Background Sync API o evento online).
 */

const DB_NAME = 'chizy-outbox';
const DB_VERSION = 1;
const STORE_NAME = 'requests';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Agrega una request a la cola offline.
 * @param {Object} request - {method, endpoint, body, token}
 * @returns {string} id del item encolado
 */
export async function enqueueRequest(request) {
  const db = await openDB();
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const item = {
    id,
    method: request.method,
    endpoint: request.endpoint,
    body: request.body,
    token: request.token || '',
    createdAt: Date.now(),
    attempts: 0,
    lastError: null,
  };
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).put(item);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  return id;
}

/**
 * Obtiene todos los items pendientes en la cola.
 * @returns {Array} lista de items
 */
export async function getPendingRequests() {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const result = await new Promise((resolve, reject) => {
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return result;
}

/**
 * Elimina un item de la cola por id.
 */
export async function removeRequest(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).delete(id);
  await new Promise((resolve, reject) => {
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

/**
 * Cuenta los items pendientes.
 */
export async function countPending() {
  const items = await getPendingRequests();
  return items.length;
}

/**
 * Procesa la cola manualmente (usado al volver online si BG Sync no disponible).
 * @param {string} token — JWT del usuario actual
 * @returns {{ processed: number, failed: number }}
 */
export async function processQueue(token) {
  const items = await getPendingRequests();
  let processed = 0;
  let failed = 0;

  for (const item of items) {
    try {
      const resp = await fetch(item.endpoint, {
        method: item.method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || item.token}`,
        },
        body: JSON.stringify(item.body),
      });

      if (resp.ok || (resp.status >= 400 && resp.status < 500)) {
        // Éxito o error del cliente: remover de cola
        await removeRequest(item.id);
        if (resp.ok) processed++;
        else failed++;
      } else {
        // Error de servidor: incrementar intentos
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const updated = { ...item, attempts: (item.attempts || 0) + 1, lastError: `HTTP ${resp.status}` };
        if (updated.attempts >= 3) {
          tx.objectStore(STORE_NAME).delete(item.id);
          failed++;
        } else {
          tx.objectStore(STORE_NAME).put(updated);
        }
        db.close();
      }
    } catch (e) {
      // Sin red: dejar en cola
      failed++;
    }
  }

  return { processed, failed };
}

/**
 * Registra el background sync tag en el SW (si está disponible).
 * Si no, el processQueue manual al volver online es el fallback.
 */
export async function scheduleBackgroundSync() {
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.sync.register('chizy-outbox');
    return true;
  } catch (e) {
    return false;
  }
}
